<template>
  <div class="contents">
    <div class="layout">

      <UserListHeader title="회원 관리" menu="회원 관리"/>

      <div class="white-panel mt20">
        <DataTable
          :columns="columns"
          :rows="items"
          row-key="uuid"
          :row-class="rowClass"
          searchable
          search-placeholder="ID 부분검색 · 이름·이메일·연락처는 정확히 입력"
          paged
          :page="page"
          :size="size"
          :total-elements="totalElements"
          :total-pages="totalPages"
          :sort-key="sortKey"
          :sort-direction="sortOrder"
          @update:page="onPage"
          @update:size="onSize"
          @sort="onSort"
          @search="onSearch"
        >
          <!-- 엑셀 다운로드: 지금 화면의 역할 필터·검색어·정렬 그대로 전체 페이지 (mimio#76) -->
          <template #toolbar-right>
            <label class="export-opt">
              <input v-model="excludeTestAccounts" type="checkbox" />
              테스트 계정 제외
            </label>
            <label class="export-opt">
              <input v-model="activeOnly" type="checkbox" />
              정상(ACTIVE) 회원만
            </label>
            <button type="button" class="export-btn" :disabled="exporting" @click="downloadExcel">
              {{ exporting ? '다운로드 중…' : '엑셀 다운로드' }}
            </button>
          </template>

          <!-- Role: 정렬(헤더 기본) + 필터 셀렉트 -->
          <template #filter-roleType>
            <select v-model="selectedRole" @change="onFilter">
              <option value="">All</option>
              <option v-for="role in roleOptions" :key="role" :value="role">{{ role }}</option>
            </select>
          </template>

          <template #cell-no="{ index }">{{ index + 1 }}</template>

          <template #cell-name="{ row }">
            <router-link :to="{ name: 'UserDetail', params: { userUuid: row.uuid } }">
              {{ row.name }}
            </router-link>
          </template>

          <template #cell-phoneNum="{ row }">{{ maskPhone(row.phoneNum) }}</template>

          <template #cell-isTestAccount="{ row }">
            <input
              type="checkbox"
              :checked="row.isTestAccount"
              title="통계에서 제외할 테스트계정으로 표시"
              @change="toggleTestAccount(row, $event.target.checked)"
            />
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useSort } from '@/composables/useSort';
import { apiClient } from '@/services/auth-header';
import { onMounted, ref } from 'vue';
import UserListHeader from './UserListHeader.vue';
import DataTable from '@/components/common/DataTable.vue';
import { maskPhone } from '@/common/mask';

const { sortKey, sortOrder, sortBy } = useSort('createdAt', 'DESC');

const items = ref([]);
const selectedRole = ref('');
const search = ref('');

const page = ref(0);
const size = ref(20);
const totalElements = ref(0);
const totalPages = ref(0);

const roleOptions = [
  'ARTIST_BEFORE_PROFILE',
  'ARTIST',
  'MEMBER',
  'ARTIST_PENDING',
  'ARTIST_PENDING_RETRY'
];

const columns = [
  { key: 'no', label: 'NO', minWidth: '50px' },
  { key: 'roleType', label: 'Role', sortable: true, minWidth: '210px' },
  { key: 'name', label: '이름', minWidth: '90px' },
  { key: 'accountId', label: 'ID', sortable: true, minWidth: '120px' },
  { key: 'email', label: '이메일', minWidth: '200px' },
  { key: 'phoneNum', label: '전화번호', minWidth: '130px' },
  { key: 'createdAt', label: '가입일시', sortable: true, minWidth: '160px' },
  { key: 'status', label: '상태', sortable: true, minWidth: '110px' },
  { key: 'isTestAccount', label: '테스트 계정', minWidth: '90px', align: 'center' },
];

const rowClass = (row) => ({
  'bg-hidden': row.status !== 'ACTIVE',
  'line-through': row.status === 'DELETED',
});

const fetchItems = async () => {
  try {
    const response = await apiClient.get('/users/paged', {
      params: {
        roleTypes: selectedRole.value || undefined,
        filter: search.value || undefined,
        page: page.value,
        size: size.value,
        sort: sortKey.value,
        direction: sortOrder.value,
      },
    });
    const d = response.data;
    items.value = d.content ?? [];
    totalElements.value = d.totalElements ?? 0;
    totalPages.value = d.totalPages ?? 0;
  } catch (error) {
    console.error('Error:', error);
  }
};

const onPage = (p) => {
  page.value = p;
  fetchItems();
};
const onSize = (s) => {
  size.value = s;
  page.value = 0;
  fetchItems();
};
const onSort = (key) => {
  sortBy(key, () => {
    page.value = 0;
    fetchItems();
  });
};
const onFilter = () => {
  page.value = 0;
  fetchItems();
};
const onSearch = (keyword) => {
  search.value = keyword;
  page.value = 0;
  fetchItems();
};

// 엑셀 다운로드 (mimio#76) — 서버가 같은 조건으로 전체 행을 만든다. 전화번호는 넣지 않는다.
const excludeTestAccounts = ref(true);
const activeOnly = ref(true);
const exporting = ref(false);

const downloadExcel = async () => {
  exporting.value = true;
  try {
    const response = await apiClient.get('/users/export', {
      params: {
        roleTypes: selectedRole.value || undefined,
        filter: search.value || undefined,
        sort: sortKey.value,
        direction: sortOrder.value,
        excludeTestAccounts: excludeTestAccounts.value,
        activeOnly: activeOnly.value,
      },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
    const role = selectedRole.value ? `_${selectedRole.value}` : '';
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `회원목록${role}_${today}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('회원 엑셀 다운로드 실패:', error);
    alert(error?.response?.status === 403 ? '회원 다운로드 권한이 없습니다.' : '엑셀 다운로드 중 오류가 발생했습니다.');
  } finally {
    exporting.value = false;
  }
};

// 테스트계정 지정/해제 (통계 제외용) — 토글은 기존 v1 유지
const toggleTestAccount = async (item, value) => {
  try {
    await apiClient.put(`/users/${item.uuid}/test-account`, null, { params: { value } });
    item.isTestAccount = value;
  } catch (error) {
    console.error('테스트계정 변경 실패:', error);
    alert('테스트계정 변경에 실패했습니다.');
  }
};

onMounted(async () => {
  await fetchItems();
});
</script>

<style scoped>
.export-opt {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #374151;
  white-space: nowrap;
  cursor: pointer;
}
.export-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #2563eb;
  background: #fff;
  color: #2563eb;
  border-radius: 4px;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
}
.export-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
